import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import {
   Box,
   Button,
   TextField,
   Switch,
   FormControlLabel,
   Stepper,
   Step,
   StepLabel,
   CircularProgress,
   Typography,
   Paper,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { toast } from 'react-toastify';
import StyledDashboardLayout from '../style';
import {
   useCreateContractTypeMutation,
   useUpdateContractTypeMutation,
   useGetContractTypeByIdQuery,
} from '../../Redux/Service/ContractTypeApi';
import {
   FIELD_CONFIG,
   convertDataToText,
   convertTextToData,
   validateFieldData,
} from '../../Utils/contractFieldHelpers';

const FIELD_NAMES = Object.keys(FIELD_CONFIG);

function ContractTypeForm({ mode = 'add' }) {
   const navigate = useNavigate();
   const { id } = useParams();
   const isEditMode = mode === 'edit';

   // State
   const [activeStep, setActiveStep] = useState(0);
   const [fieldTexts, setFieldTexts] = useState({});
   const [fieldErrors, setFieldErrors] = useState({}); // NEW: holds live per-field error text

   // API hooks
   const [createContractType, { isLoading: isCreating }] = useCreateContractTypeMutation();
   const [updateContractType, { isLoading: isUpdating }] = useUpdateContractTypeMutation();

   // Fetch existing data in edit mode
   const {
      data: contractTypeResponse,
      isLoading: isFetching,
      isError: fetchError,
   } = useGetContractTypeByIdQuery(id, {
      skip: !isEditMode || !id,
   });

   const contractType = contractTypeResponse?.response;

   // Initialize field texts from fetched data
   useEffect(() => {
      if (isEditMode && contractType?.field_values && Array.isArray(contractType.field_values)) {
         const textMap = {};
         contractType.field_values.forEach((field) => {
            textMap[field.field_name] = convertDataToText(field.data, field.field_name);
         });
         setFieldTexts(textMap);
      }
   }, [isEditMode, contractType?.field_values, contractType?.id]);

   // Steps configuration
   const steps = useMemo(() => {
      return ['Basic Information', ...FIELD_NAMES];
   }, []);

   // Initial values
   const initialValues = useMemo(() => {
      if (isEditMode && contractType) {
         return {
            name: contractType.name || '',
            description: contractType.description || '',
            is_active: contractType.is_active ?? true,
         };
      }
      return {
         name: '',
         description: '',
         is_active: true,
      };
   }, [isEditMode, contractType]);

   // Validation schema for basic info (step 0)
   const basicInfoValidationSchema = Yup.object().shape({
      name: Yup.string()
         .required('Contract Type Name is required')
         .trim('Contract Type Name cannot contain only spaces')
         .typeError('Contract Type Name must be a string')
         .test('no-spaces-only', 'Contract Type Name cannot contain only spaces', function (value) {
            if (!value) return false;
            return value.trim().length > 0;
         })
         .min(2, 'Name must be at least 2 characters (after trimming)')
         .max(100, 'Name must not exceed 100 characters'),
      description: Yup.string().nullable().trim('Description will be trimmed').max(1000, "Description should be atmost 1000 characters"),
      is_active: Yup.boolean(),
   });

   // Validate current step
   const validateStep = useCallback(
      (stepIndex, values) => {
         if (stepIndex === 0) {
            // Validate basic info using Yup
            try {
               basicInfoValidationSchema.validateSync(values, { abortEarly: false });
               return { isValid: true, errors: null };
            } catch (err) {
               const errors = {};
               err.inner.forEach((error) => {
                  errors[error.path] = error.message;
               });
               return { isValid: false, errors };
            }
         } else {
            // Validate field value
            const fieldName = FIELD_NAMES[stepIndex - 1];
            const fieldText = fieldTexts[fieldName] || '';
            const validation = validateFieldData(fieldText, fieldName);

            if (!validation.isValid) {
               return { isValid: false, errors: { [fieldName]: validation.error } };
            }
            return { isValid: true, errors: null };
         }
      },
      [fieldTexts, basicInfoValidationSchema],
   );
   // Handle next button
   const handleNext = useCallback(
      (values) => {
         const validation = validateStep(activeStep, values);

         if (!validation.isValid) {
            // Show first error
            const firstError = Object.values(validation.errors)[0];
            toast.error(firstError);
            return;
         }

         if (activeStep < steps.length - 1) {
            setActiveStep((prev) => prev + 1);
         }
      },
      [activeStep, steps.length, validateStep],
   );

   // Handle back button
   const handleBack = useCallback(() => {
      if (activeStep > 0) {
         setActiveStep((prev) => prev - 1);
      }
   }, [activeStep]);

   // Handle form submission
   const handleSubmit = useCallback(
      async (values) => {
         // Final validation of all steps
         for (let i = 0; i < steps.length; i++) {
            const validation = validateStep(i, values);
            if (!validation.isValid) {
               toast.error(`Step ${i + 1}: ${Object.values(validation.errors)[0]}`);
               setActiveStep(i);
               return;
            }
         }

         // Trim name and description
         const trimmedName = (values.name || '').trim();
         const trimmedDescription = (values.description || '').trim();

         // Additional validation after trimming
         if (!trimmedName || trimmedName.length < 2) {
            toast.error('Contract Type Name must be at least 2 characters (after trimming)');
            setActiveStep(0);
            return;
         }

         if (trimmedName.length > 255) {
            toast.error('Contract Type Name must not exceed 255 characters');
            setActiveStep(0);
            return;
         }

         // Build field_values object
         const field_values = {};
         FIELD_NAMES.forEach((fieldName) => {
            const fieldText = fieldTexts[fieldName] || '';
            field_values[fieldName] = convertTextToData(fieldText, fieldName);
         });

         // Prepare request data with trimmed values
         const requestData = {
            name: trimmedName,
            description: trimmedDescription,
            is_active: values.is_active,
            field_values,
         };

         try {
            let response;
            if (isEditMode) {
               response = await updateContractType({ id: Number(id), data: requestData }).unwrap();
            } else {
               response = await createContractType(requestData).unwrap();
            }

            toast.success(
               response?.message ||
               `Contract Type ${isEditMode ? 'updated' : 'created'} successfully`,
            );
            navigate('/contract-type/list');
         } catch (error) {
            const errorMsg =
               error?.data?.error || error?.data?.message || 'An error occurred. Please try again.';
            toast.error(errorMsg);
            console.error('Error submitting contract type:', error);
         }
      },
      [
         steps.length,
         validateStep,
         fieldTexts,
         isEditMode,
         updateContractType,
         createContractType,
         id,
         navigate,
      ],
   );

   // Handle field text change
   const handleFieldTextChange = useCallback((fieldName, value) => {
      setFieldTexts((prev) => ({
         ...prev,
         [fieldName]: value,
      }));

      // NEW: live-validate as the user types, store error to show under the field
      const validation = validateFieldData(value, fieldName);
      setFieldErrors((prev) => ({
         ...prev,
         [fieldName]: validation.isValid ? '' : validation.error,
      }));
   }, []);

   // Handle home/back button in header
   const handleHomeClick = useCallback(() => {
      if (activeStep === 0) {
         navigate('/contract-type/list');
      } else {
         setActiveStep((prev) => prev - 1);
      }
   }, [activeStep, navigate]);

   // Render step content
   const renderStepContent = useCallback(
      (stepIndex, values, setFieldValue, errors, touched, handleBlur) => {
         if (stepIndex === 0) {
            // Basic Information Step
            return (
               <Box sx={{ maxWidth: '600px', mx: 'auto' }}>
                  <TextField
                     fullWidth
                     label="Contract Type Name"
                     name="name"
                     value={values.name}
                     onChange={(e) => setFieldValue('name', e.target.value)}
                     onBlur={(e) => {
                        // Trim on blur
                        const trimmedValue = e.target.value.trim();
                        setFieldValue('name', trimmedValue);
                        handleBlur(e);
                     }}
                     error={touched.name && Boolean(errors.name)}
                     helperText={touched.name && errors.name}
                     required
                     sx={{ mb: 3 }}
                  />

                  <TextField
                     fullWidth
                     label="Description"
                     name="description"
                     value={values.description}
                     onChange={(e) => setFieldValue('description', e.target.value)}
                     onBlur={(e) => {
                        // Trim on blur
                        const trimmedValue = e.target.value.trim();
                        setFieldValue('description', trimmedValue);
                        handleBlur(e);
                     }}
                     helperText={touched.description && errors.description}
                     error={touched.description && Boolean(errors.description)}
                     multiline
                     rows={4}
                     sx={{ mb: 3 }}
                  />
                  {
                     values.description?.length > 0 && (
                        <Typography sx={{ color: '#666' }}>
                           {values.description?.length} characters
                        </Typography>
                     )
                  }
                  <FormControlLabel
                     control={
                        <Switch
                           checked={values.is_active}
                           onChange={(e) => setFieldValue('is_active', e.target.checked)}
                           name="is_active"
                        />
                     }
                     label="Active"
                  />
               </Box>
            );
         } else {
            // Field Value Steps
            const fieldName = FIELD_NAMES[stepIndex - 1];
            const config = FIELD_CONFIG[fieldName];
            const fieldText = fieldTexts[fieldName] || '';

            return (
               <Box sx={{ maxWidth: '800px', mx: 'auto' }}>
                  <Paper sx={{ p: 3, backgroundColor: '#f8f9fa' }}>
                     <Typography variant="h6" sx={{ mb: 2, color: '#3F30FF', fontWeight: 600 }}>
                        {fieldName}
                     </Typography>
                     <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>
                        {config.description}
                     </Typography>

                     <TextField
                        fullWidth
                        label={fieldName}
                        value={fieldText}
                        onChange={(e) => handleFieldTextChange(fieldName, e.target.value)}
                        error={Boolean(fieldErrors[fieldName])}
                        helperText={fieldErrors[fieldName] || ''}
                        multiline
                        rows={config.type === 'array' ? 10 : 8}
                        placeholder={
                           config.type === 'array'
                              ? 'Enter each item on a new line'
                              : 'Enter text content'
                        }
                        sx={{
                           '& .MuiOutlinedInput-root': {
                              fontFamily: 'monospace',
                              fontSize: '14px',
                           },
                        }}
                     />
                     {
                        fieldText?.length > 0 && (
                           <Typography sx={{ color: '#666' }}>
                              {fieldText?.length} characters
                           </Typography>
                        )
                     }

                     {config.type === 'array' && (
                        <Typography
                           variant="caption"
                           sx={{ mt: 1, display: 'block', color: '#999' }}
                        >
                           Tip: Each line will be treated as a separate item
                        </Typography>
                     )}
                  </Paper>
               </Box>
            );
         }
      },
      [fieldTexts, fieldErrors, handleFieldTextChange],
   );

   // Loading states
   if (isEditMode && isFetching) {
      return (
         <StyledDashboardLayout>
            <div className="content-wrapper">
               <Box
                  sx={{
                     display: 'flex',
                     justifyContent: 'center',
                     alignItems: 'center',
                     minHeight: '400px',
                  }}
               >
                  <CircularProgress />
               </Box>
            </div>
         </StyledDashboardLayout>
      );
   }

   if (isEditMode && fetchError) {
      return (
         <StyledDashboardLayout>
            <div className="content-wrapper">
               <Box sx={{ p: 3 }}>
                  <Typography color="error">Failed to load contract type data</Typography>
                  <Button onClick={() => navigate('/contract-type/list')} sx={{ mt: 2 }}>
                     Back to List
                  </Button>
               </Box>
            </div>
         </StyledDashboardLayout>
      );
   }

   const isLoading = isCreating || isUpdating;

   return (
      <StyledDashboardLayout>
         <div className="content-wrapper">
            {isLoading ? (
               <div
                  className="loading-screen"
                  style={{
                     display: 'flex',
                     flexDirection: 'column',
                     alignItems: 'center',
                     justifyContent: 'center',
                     minHeight: '400px',
                  }}
               >
                  <CircularProgress />
                  <p>Processing your request. Please wait...</p>
               </div>
            ) : (
               <>
                  <div className="content-header">
                     <div className="container-fluid">
                        <div className="d-flex justify-content-between mb-2">
                           <div className="pageTitle">
                              <h1 className="m-0">{isEditMode ? 'Edit' : 'Add'} Contract Type</h1>
                           </div>

                           <Button
                              onClick={handleHomeClick}
                              startIcon={<ArrowBackIcon />}
                              sx={{
                                 display: 'flex',
                                 alignItems: 'center',
                                 gap: '8px',
                                 padding: '10px 20px',
                                 minWidth: '160px',
                                 height: '44px',
                                 backgroundColor: '#3F30FF',
                                 borderRadius: '8px',
                                 color: '#ffffff',
                                 border: '2px solid #3F30FF',
                                 textTransform: 'none',
                                 fontSize: '14px',
                                 fontWeight: '600',
                                 letterSpacing: '0.5px',
                                 boxShadow: '0 4px 12px rgba(63, 48, 255, 0.4)',
                                 cursor: 'pointer',
                                 transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                 position: 'relative',
                                 overflow: 'hidden',
                                 '&:before': {
                                    content: '""',
                                    position: 'absolute',
                                    top: 0,
                                    left: '-100%',
                                    width: '100%',
                                    height: '100%',
                                    background:
                                       'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
                                    transition: 'left 0.5s',
                                 },
                                 '&:hover': {
                                    backgroundColor: '#3F30FF',
                                    borderColor: '#3F30FF',
                                    '&:before': {
                                       left: '100%',
                                    },
                                 },
                                 '& .MuiButton-startIcon': {
                                    marginRight: '8px',
                                    fontSize: '18px',
                                    color: '#ffffff',
                                    transition: 'transform 0.3s ease',
                                 },
                                 '&:hover .MuiButton-startIcon': {
                                    transform: 'translateX(-2px)',
                                 },
                              }}
                           >
                              {activeStep === 0
                                 ? 'Back to Contract Types'
                                 : `Back to ${steps[activeStep - 1]}`}
                           </Button>
                        </div>
                     </div>
                  </div>

                  <div className="card customCard p-3 m-3">
                     <Box sx={{ width: '100%' }}>
                        <Stepper activeStep={activeStep} sx={{ marginBottom: '20px' }}>
                           {steps.map((label, index) => {
                              const labelProps = {};

                              if (index === activeStep) {
                                 labelProps.sx = {
                                    color: '#3F30FF',
                                    fontWeight: 'bold',
                                 };
                              }

                              return (
                                 <Step
                                    key={label}
                                    onClick={() => {
                                       if (index < activeStep) {
                                          setActiveStep(index);
                                       }
                                    }}
                                    sx={{
                                       cursor: index < activeStep ? 'pointer' : 'default',
                                       '&:hover': {
                                          backgroundColor:
                                             index < activeStep ? '#f5f5f5' : 'transparent',
                                       },
                                    }}
                                 >
                                    <StepLabel {...labelProps}>{label}</StepLabel>
                                 </Step>
                              );
                           })}
                        </Stepper>

                        <Formik
                           initialValues={initialValues}
                           validationSchema={activeStep === 0 ? basicInfoValidationSchema : null}
                           enableReinitialize={isEditMode}
                        >
                           {({ values, setFieldValue, errors, touched, handleBlur }) => (
                              <Form>
                                 {renderStepContent(
                                    activeStep,
                                    values,
                                    setFieldValue,
                                    errors,
                                    touched,
                                    handleBlur,
                                 )}

                                 <Box
                                    sx={{
                                       display: 'flex',
                                       flexDirection: 'row',
                                       gap: '10px',
                                       pt: 2,
                                    }}
                                 >
                                    <Button
                                       color="inherit"
                                       disabled={activeStep === 0}
                                       onClick={handleBack}
                                       sx={{ mr: 1 }}
                                       className="backBtn"
                                    >
                                       Back
                                    </Button>
                                    <Box />
                                    {activeStep === steps.length - 1 ? (
                                       <Button
                                          type="button"
                                          onClick={() => handleSubmit(values)}
                                          className="commonBtn"
                                       >
                                          Submit
                                       </Button>
                                    ) : (
                                       <Button
                                          type="button"
                                          onClick={() => handleNext(values)}
                                          className="commonBtn"
                                       >
                                          Next
                                       </Button>
                                    )}
                                 </Box>
                              </Form>
                           )}
                        </Formik>
                     </Box>
                  </div>
               </>
            )}
         </div>
      </StyledDashboardLayout>
   );
}

export default React.memo(ContractTypeForm);